import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import { 
  TrendingUp, 
  Rocket, 
  Users, 
  BarChart3, 
  Handshake,
  ChevronLeft,
  ChevronRight,
  Bot,
  Palette,
  BarChart,
  Zap,
  Search,
  Layers,
  UserPlus
} from 'lucide-react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './OrganizerLanding.css';
import OrganizerNavbar from './OrganizerNavbar';
import OrganizerFooter from './OrganizerFooter';

const OrganizerLanding = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const personas = [
    {
      id: 'investor',
      title: 'Investor',
      description: 'Evaluate startups and founders with AI-powered screening sessions. Assess business models, market potential, and founder readiness.',
      Icon: TrendingUp,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      features: ['Startup Evaluation', 'Founder Assessment', 'Investment Readiness']
    },
    {
      id: 'founder',
      title: 'Founder',
      description: 'Prepare for investor meetings with realistic interview simulations. Practice your pitch and get feedback on your business strategy.',
      Icon: Rocket,
      color: '#f093fb',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      features: ['Investor Readiness', 'Pitch Practice', 'Business Strategy']
    },
    {
      id: 'recruiter',
      title: 'Recruiter',
      description: 'Streamline candidate screening with AI interviews. Save time while identifying top talent through structured assessments.',
      Icon: Users,
      color: '#4facfe',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      features: ['Candidate Screening', 'Talent Assessment', 'Time Saving']
    },
    {
      id: 'hr_manager',
      title: 'HR Manager',
      description: 'Conduct comprehensive employee assessments and evaluations. Track performance and identify development opportunities.',
      Icon: BarChart3,
      color: '#43e97b',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      features: ['Employee Assessment', 'Performance Tracking', 'Development Planning']
    },
    {
      id: 'vendor',
      title: 'Vendor',
      description: 'Evaluate potential vendors and partners systematically. Assess capabilities, reliability, and alignment with your needs.',
      Icon: Handshake,
      color: '#fa709a',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      features: ['Vendor Evaluation', 'Partner Assessment', 'Capability Analysis']
    }
  ];

  const CustomPrevArrow = ({ onClick }) => (
    <button className="organizer-carousel-arrow organizer-carousel-prev" onClick={onClick}>
      <ChevronLeft size={24} />
    </button>
  );

  const CustomNextArrow = ({ onClick }) => (
    <button className="organizer-carousel-arrow organizer-carousel-next" onClick={onClick}>
      <ChevronRight size={24} />
    </button>
  );

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    beforeChange: (current, next) => setCurrentSlide(next),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const features = [
    {
      title: 'AI-Powered Interviews',
      description: 'Conduct realistic interviews with AI personas tailored to your needs',
      Icon: Bot,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea'
    },
    {
      title: 'Custom Branding',
      description: 'Personalize the interview experience with your company branding',
      Icon: Palette,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f5576c'
    },
    {
      title: 'Detailed Analytics',
      description: 'Get comprehensive insights and performance metrics for each session',
      Icon: BarChart,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe'
    },
    {
      title: 'Easy Management',
      description: 'Create, manage, and track multiple screening sessions effortlessly',
      Icon: Zap,
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      color: '#fa709a'
    }
  ];

  return (
    <div className="organizer-landing">
      <OrganizerNavbar />
      
      {/* Hero Section */}
      <section className="organizer-hero">
        <div className="organizer-hero-overlay"></div>
        <div className="organizer-hero-container">
          <div className="organizer-hero-content">
            <h1 className="organizer-hero-title">
              Transform Your Screening <br/> Process with AI
            </h1>
            <p className="organizer-hero-subtitle">
              Create personalized interview sessions, assess candidates, founders,<br/> or partners with  AI-powered personas designed for your specific needs.
            </p>
            <div className="organizer-hero-buttons">
              <button 
                className="organizer-btn-primary"
                onClick={() => navigate('/organizer/register')}
              >
                Get Started Free
              </button>
              <button 
                className="organizer-btn-secondary"
                onClick={() => navigate('/organizer/login')}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Personas Section */}
      <section className="organizer-personas">
        <div className="organizer-section-container">
          <h2 className="organizer-section-title">Choose Your Persona</h2>
          <p className="organizer-section-subtitle">
            Select the role that best matches your screening needs
          </p>
          <div className="organizer-personas-carousel-wrapper">
            <Slider {...carouselSettings} className="organizer-personas-carousel">
              {personas.map((persona) => {
                const IconComponent = persona.Icon;
                return (
                  <div key={persona.id} className="organizer-persona-slide">
                    <div 
                      className="organizer-persona-card"
                      style={{ '--persona-gradient': persona.gradient, '--persona-color': persona.color }}
                    >
                      <div className="organizer-persona-icon-wrapper">
                        <IconComponent className="organizer-persona-icon" size={48} />
                      </div>
                      <h3 className="organizer-persona-title">{persona.title}</h3>
                      <p className="organizer-persona-description">{persona.description}</p>
                      <ul className="organizer-persona-features">
                        {persona.features.map((feature, idx) => (
                          <li key={idx}>
                            <span className="organizer-feature-check">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="organizer-features">
        <div className="organizer-features-container">
          <div className="organizer-features-content">
            <h2 className="organizer-features-title">Powerful Features</h2>
            <p className="organizer-features-subtitle">
              Everything you need to conduct effective screening sessions
            </p>
           
            <p className="organizer-features-subtitle">
              Get instant feedback and analytics for every candidate.
            </p>
            <p className="organizer-features-subtitle">
              Ensure fairness and consistency across your entire screening process.
            </p>
          </div>
          <div className="organizer-features-cards-wrapper">
            {features.map((feature, idx) => {
              const IconComponent = feature.Icon;
              return (
                <div 
                  key={idx} 
                  className="organizer-feature-card"
                  style={{ 
                    '--feature-gradient': feature.gradient, 
                    '--feature-color': feature.color 
                  }}
                >
                  <div className="organizer-feature-header">
                    <div className="organizer-feature-icon-wrapper">
                      <IconComponent className="organizer-feature-icon" size={32} />
                    </div>
                    <h3 className="organizer-feature-title">{feature.title}</h3>
                  </div>
                  <p className="organizer-feature-description">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="organizer-how-it-works">
        <div className="organizer-section-container">
          <h2 className="organizer-how-it-works-title">How it works</h2>
          <p className="organizer-how-it-works-subtitle">
            Our service uses advanced AI technologies for instant candidate screening across multiple interview sessions
          </p>
          <div className="organizer-steps-progress">
            <div className="organizer-progress-line"></div>
            <div className="organizer-progress-circle organizer-progress-circle-1">1</div>
            <div className="organizer-progress-circle organizer-progress-circle-2">2</div>
            <div className="organizer-progress-circle organizer-progress-circle-3">3</div>
          </div>
          <div className="organizer-steps-cards">
            <div className="organizer-step-card">
              <div className="organizer-step-icon-wrapper">
                <UserPlus className="organizer-step-icon" size={24} />
              </div>
              <h3 className="organizer-step-card-title">Sign Up & Choose Persona</h3>
              <p className="organizer-step-card-description">
                Create your account and select the persona that matches your role, and we'll instantly set up your workspace.
              </p>
              <ul className="organizer-step-card-list">
                <li>Smart persona selection understands your needs</li>
                <li>Automatic workspace configuration</li>
                <li>Quick access to your dashboard</li>
              </ul>
            </div>
            <div className="organizer-step-card">
              <div className="organizer-step-icon-wrapper">
                <Layers className="organizer-step-icon" size={24} />
              </div>
              <h3 className="organizer-step-card-title">Create Screening Session</h3>
              <p className="organizer-step-card-description">
                Set up your interview session, customize settings, and add participants with ease.
              </p>
              <ul className="organizer-step-card-list">
                <li>Sort by date, status and participant count</li>
                <li>Filter by persona type and session type</li>
                <li>Detailed information about each session</li>
              </ul>
            </div>
            <div className="organizer-step-card">
              <div className="organizer-step-icon-wrapper">
                <Zap className="organizer-step-icon" size={24} />
              </div>
              <h3 className="organizer-step-card-title">Review Results</h3>
              <p className="organizer-step-card-description">
                Analyze comprehensive reports and insights from each screening session instantly.
              </p>
              <ul className="organizer-step-card-list">
                <li>Direct access to detailed analytics</li>
                <li>Export results capability</li>
                <li>Track participant performance over time</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="organizer-cta">
        <div className="organizer-cta-container">
          <h2 className="organizer-cta-title">Ready to Get Started?</h2>
          <p className="organizer-cta-subtitle">
            Join thousands of organizers who are transforming their screening process
          </p>
          <button 
            className="organizer-btn-primary organizer-btn-large"
            onClick={() => navigate('/organizer/register')}
          >
            Start Free Today
          </button>
        </div>
      </section> */}

      <OrganizerFooter />
    </div>
  );
};

export default OrganizerLanding;

